import { computed, inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { toSignal } from '@angular/core/rxjs-interop';
import { catchError, filter, map, Observable, of, switchMap, tap } from 'rxjs';
import { ModalService } from '@portal/app/dashboard/context-modal/services/modal.service';
import { compact, isEmpty } from 'lodash-es';
import {
  AxisLabelsFormatterCallbackFunction,
  Options,
  Point,
  SeriesOptionsType,
  TooltipFormatterCallbackFunction,
  XAxisOptions,
  YAxisOptions
} from 'highcharts';
import {
  ChartDataPoint,
  ChartScatterDataPoint
} from '@portal/app/mpo/shared/types/chart.types';
import { CurrencyPipe } from '@angular/common';
import { AbbreviateNumberPipe } from '@portal/app/shared/pipes/abbreviate-number.pipe';
import { CurrencyService } from '@libs/common-services';
import { baseChartConfig } from '@design-system/components/m-line-chart';
import {
  TacticReturnCurveDataPoint,
  TacticReturnCurveResponseDTO
} from '@libs/api';
import { ViewBy } from '@portal/app/shared/constants';
import { ReturnCurveChartSeriesHelper } from '@portal/app/shared/services/return-curve-chart-series-helper';
import { TacticReturnCurveRequestDTO } from '@portal/app/dashboard/context-modal/context-model.types';

interface ReturnCurveChartResponse {
  response: TacticReturnCurveResponseDTO;
  chartOptions: Options;
}

interface CustomPoint extends Highcharts.Point {
  point: {
    y: number | null;
    yFormatted: string | null;
  };
}

@Injectable()
export class ReturnCurveChartService {
  private readonly http: HttpClient = inject(HttpClient);
  private readonly modalService: ModalService = inject(ModalService);

  private currencyService = inject(CurrencyService);
  private currencyPipe = inject(CurrencyPipe);

  private readonly LIGHT_GREY_HEX = '#8d98a8';

  private initialValue: ReturnCurveChartResponse = {
    response: {} as TacticReturnCurveResponseDTO,
    chartOptions: {} as Options
  };

  private isLoadingSignal = signal<boolean>(false);

  public data = toSignal(this.fetchData$(), {
    initialValue: this.initialValue
  });

  chartOptions = computed(() => {
    return this.data().chartOptions;
  });

  get isLoading() {
    return this.isLoadingSignal.asReadonly();
  }

  isChartReady = computed(() => {
    return !this.isLoading() && !isEmpty(this.data().chartOptions);
  });

  isChartValid = computed(() => {
    // You may get a flat curve if the a,b parameters are null for the tactic.
    const isCurveValid =
      typeof this.data().response.curveParams.a === 'number' &&
      !isNaN(this.data().response.curveParams.a);

    return !this.isLoading() && isCurveValid;
  });

  metricMap = ReturnCurveChartSeriesHelper.metricMap;

  private getAbbreviatePipe(currencyPipe: CurrencyPipe) {
    return new AbbreviateNumberPipe(currencyPipe);
  }

  getParams() {
    const modalParams = this.modalService.modalParams();
    return {
      ...this.modalService.getParams(),
      channel: modalParams.channel,
      tactic: modalParams.tactic,
      conversionType: this.modalService.conversionType(),
      dateStart: modalParams.dateStart,
      dateStop: modalParams.dateStop,
      viewType: this.modalService.returnCurveViewType()
    } as TacticReturnCurveRequestDTO;
  }

  fetchData$(): Observable<ReturnCurveChartResponse> {
    const url = `${ModalService.baseUriV1}/return-curve`;

    return this.modalService.modalParams$.pipe(
      filter(
        () => !!this.getParams().channel && !!this.getParams().conversionType
      ),
      tap(() => this.isLoadingSignal.set(true)),
      switchMap(() =>
        this.http
          .post<TacticReturnCurveResponseDTO>(url, this.getParams())
          .pipe(
            catchError((error) => {
              console.error('Error fetching top metric data', error);
              this.isLoadingSignal.set(false);
              return of({
                data: [] as TacticReturnCurveDataPoint[]
              } as TacticReturnCurveResponseDTO);
            }),
            map((response) => ({
              response,
              chartOptions: this.getChartOptions(
                response,
                ViewBy.cpo,
                this.currencyPipe
              )
            }))
          )
      ),
      tap(() => this.isLoadingSignal.set(false))
    );
  }

  private mapResponseData(data: TacticReturnCurveDataPoint[], viewBy: ViewBy) {
    // sorted by Media Spend ASCENDING order
    const spendAscBundledData =
      ReturnCurveChartSeriesHelper.sortDataBySpendInAsc(data);

    return {
      primaryData: ReturnCurveChartSeriesHelper.preparePrimarySeriesData(
        spendAscBundledData,
        viewBy
      ),
      secondaryData: ReturnCurveChartSeriesHelper.prepareSecondarySeriesData(
        spendAscBundledData,
        viewBy
      ),
      scatterPlotData: ReturnCurveChartSeriesHelper.createScatterPlotData(
        data,
        viewBy
      ),
      bundledData: spendAscBundledData
    };
  }

  public getChartOptions(
    response: TacticReturnCurveResponseDTO,
    viewBy: ViewBy,
    currencyPipe: CurrencyPipe
  ) {
    if (!response?.data?.length) {
      return {};
    }
    const data = this.mapResponseData(response.data, viewBy);
    if (response.curveParams.modelInfo === 'spend') {
      data.scatterPlotData = [];
    }
    return this.formatChartData(
      data.bundledData,
      data.primaryData,
      data.secondaryData,
      data.scatterPlotData,
      viewBy,
      currencyPipe
    );
  }

  formatChartData(
    bundledData: TacticReturnCurveDataPoint[],
    primaryData: ChartDataPoint[],
    secondaryData: ChartDataPoint[],
    scatterPlotData: ChartScatterDataPoint[],
    viewBy: ViewBy,
    currencyPipe: CurrencyPipe
  ): Options {
    const chartOptions = {
      series: this.createSeriesData(
        primaryData,
        secondaryData,
        scatterPlotData,
        viewBy
      ) as SeriesOptionsType[],
      xAxis: this.createXAxisFormatters(bundledData, currencyPipe),
      yAxis: this.createYAxisFormatters(
        primaryData,
        secondaryData,
        scatterPlotData,
        viewBy,
        currencyPipe
      ),
      tooltip: {
        shared: true,
        useHTML: true,
        formatter: this.createTooltipFormatter(),
        positioner: function (
          labelWidth: number,
          labelHeight: number,
          point: Point
        ) {
          const x = point.plotX !== undefined ? point.plotX : 0;
          const offset = 2;
          const tooltip = this as unknown as Highcharts.Tooltip;

          // Calculate tooltip position
          let tooltipX = x - labelWidth / 2; // Center horizontally
          const tooltipY = 0 + offset; // top of chart

          // Adjust for right edge clipping
          if (tooltipX + labelWidth > tooltip.chart.chartWidth) {
            tooltipX = tooltip.chart.plotWidth - labelWidth - offset; // Move to the left
          }

          // Adjust for left edge clipping
          if (tooltipX < 0) {
            tooltipX = 0; // Move to the right
          }

          return {
            x: tooltipX,
            y: tooltipY
          };
        }
      },
      legend: { ...baseChartConfig.legend }
    };

    return chartOptions as Options;
  }

  private createSeriesData(
    primaryData: ChartDataPoint[],
    secondaryData: ChartDataPoint[],
    scatterPlotData: ChartScatterDataPoint[],
    viewBy: ViewBy
  ): SeriesOptionsType[] {
    const primarySeries = ReturnCurveChartSeriesHelper.getPrimarySeries(
      primaryData,
      viewBy
    );

    const secondarySeries = ReturnCurveChartSeriesHelper.getSecondarySeries(
      secondaryData,
      viewBy
    );

    const scatterPlotSeries = ReturnCurveChartSeriesHelper.getScatterPlotSeries(
      scatterPlotData,
      viewBy
    );

    const latestFourScatterPlotSeries =
      ReturnCurveChartSeriesHelper.getLatestFourScatterPlotSeries(
        scatterPlotData,
        viewBy
      );

    return compact([
      scatterPlotData.length ? scatterPlotSeries : null,
      scatterPlotData.length ? latestFourScatterPlotSeries : null,
      primarySeries,
      secondarySeries
    ]) as SeriesOptionsType[];
  }

  private createXAxisFormatters(
    bundledData: TacticReturnCurveDataPoint[],
    currencyPipe: CurrencyPipe
  ): XAxisOptions {
    const maxSpend = ReturnCurveChartSeriesHelper.getXAxisMaxValue(bundledData);
    const abbreviatePipe = this.getAbbreviatePipe(currencyPipe);
    const currencyConfig = this.currencyService.currencyConfig;

    const xAxisFormatters = function (): string {
      const value = this?.value as number;
      return abbreviatePipe.transform(value, currencyConfig.code, 'symbol');
    } as AxisLabelsFormatterCallbackFunction;

    const xAxisOptions = {
      title: {
        text: 'Weekly Media Spend', // X-axis label
        style: {
          color: '#8d98a8' // grey
        }
      },
      lineColor: this.LIGHT_GREY_HEX, // x-axis line
      tickColor: this.LIGHT_GREY_HEX, // x-axis ticks
      min: 1,
      max: maxSpend,
      labels: {
        formatter: xAxisFormatters,
        style: {
          color: this.LIGHT_GREY_HEX
        }
      }
    };

    return xAxisOptions as XAxisOptions;
  }

  private createYAxisFormatters(
    primaryData: ChartDataPoint[],
    secondaryData: ChartDataPoint[],
    scatterPlotData: ChartScatterDataPoint[],
    viewBy: ViewBy,
    currencyPipe: CurrencyPipe
  ): YAxisOptions {
    const abbreviatePipe = this.getAbbreviatePipe(currencyPipe);
    const currencyConfig = this.currencyService.currencyConfig;
    const yAxisPrimaryFormatters = function (): string {
      const value = this?.value as number;
      return abbreviatePipe.transform(
        value,
        viewBy === ViewBy.roas ? currencyConfig.code : undefined,
        'symbol'
      );
    } as AxisLabelsFormatterCallbackFunction;

    const yAxisSecondaryFormatters = function (): string {
      const value = this?.value as number;
      return abbreviatePipe.transform(value, currencyConfig.code, 'symbol');
    } as AxisLabelsFormatterCallbackFunction;

    const { primaryMax, secondaryMax } =
      ReturnCurveChartSeriesHelper.getYAxisMaxValues(
        primaryData,
        secondaryData,
        scatterPlotData
      );

    const yAxisOptions = [
      {
        title: {
          text: scatterPlotData.length
            ? this.metricMap[viewBy].platformMetric.label
            : this.metricMap[viewBy].metricKey2.label,
          style: {
            color: '#8d98a8' // grey
          }
        },
        opposite: false,
        lineColor: '#8d98a8', // y-axis line
        tickColor: '#8d98a8', // y-axis ticks
        min: 0,
        max: primaryMax,
        labels: {
          formatter: yAxisPrimaryFormatters,
          style: {
            color: this.LIGHT_GREY_HEX
          }
        }
      },
      {
        title: {
          text: this.metricMap[viewBy].metricKey3.label,
          style: {
            color: '#8d98a8' // grey
          }
        },
        opposite: true,
        lineColor: '#8d98a8', // y-axis line
        tickColor: '#8d98a8', // y-axis ticks
        min: 0,
        max: secondaryMax,
        labels: {
          formatter: yAxisSecondaryFormatters,
          style: {
            color: this.LIGHT_GREY_HEX
          }
        }
      }
    ];

    return yAxisOptions as YAxisOptions;
  }

  /**
   * Create the tooltip formatter function.
   *
   * @returns The tooltip formatter callback function.
   */
  private createTooltipFormatter(): TooltipFormatterCallbackFunction {
    return function (): string | false {
      if (!this.points || !this.points.length) {
        if (this.point) {
          const customScatterPoint = this.point as unknown as CustomPoint;
          const {
            point: {
              options: { xFormatted, yFormatted, seriesName, weekStartDate }
            }
          } = this as unknown as {
            point: {
              options: {
                xFormatted: string;
                yFormatted: string;
                seriesName: string;
                weekStartDate: string;
              };
            };
          };

          let tooltipHtml = `<div class="tooltip-body">`;
          tooltipHtml += `<span class="b3 text-gray-000 text-center block">Week Start Date: ${weekStartDate} </span>`;
          tooltipHtml += `<div class="m-divider dark my-2"></div>`;
          tooltipHtml += `<div class="flex justify-between items-center">`;
          tooltipHtml += `<div><span style="color:${customScatterPoint?.color}; font-size: 12px;">\u25CF</span>`;
          tooltipHtml += `<span class="b1 text-gray-000 ml-1">Media Spend:</span>:</div>`;
          tooltipHtml += `<span class="b1 text-gray-000">${xFormatted}</span>`;
          tooltipHtml += `</div>`;
          tooltipHtml += `<div class="flex justify-between items-center">`;
          tooltipHtml += `<div><span style="color:${customScatterPoint?.color}; font-size: 12px;">\u25CF</span>`;
          tooltipHtml += `<span class="b1 text-gray-000 ml-1">${seriesName}</span>:</div>`;
          tooltipHtml += `<span class="b1 text-gray-000">${yFormatted}</span>`;
          tooltipHtml += `</div>`;
          tooltipHtml += `</div>`;
          return tooltipHtml;
        } else {
          return false;
        }
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const {
        point: { xFormatted }
      } = this as unknown as { point: { xFormatted: string } };

      const spendValue = xFormatted;
      let tooltipHtml = `<div class="tooltip-body">`;

      // Return Curve(s) Tooltip - Groups the (2) curves into (1) Tooltip
      // possible with the `points` key

      tooltipHtml += `<span class="b3 text-gray-000 text-center block">Media Spend: ${spendValue}</span>`;
      tooltipHtml += `<div class="m-divider dark my-2"></div>`;

      // Sort the points by y-value DESC order so tooltip order matches corresponding curve
      this?.points?.sort((a, b) => {
        if (a?.y !== null && a?.y !== undefined) {
          return (b?.y as number) - a.y;
        }
        return 0; // If y is null or undefined, don't change the order
      });

      this?.points?.forEach((point) => {
        const customPoint = point as unknown as CustomPoint;

        if (
          customPoint?.point?.y !== null &&
          customPoint?.point?.y !== undefined &&
          customPoint?.point?.yFormatted !== null &&
          customPoint?.point?.yFormatted !== undefined
        ) {
          const seriesName = customPoint?.series?.name;
          const formattedValue = customPoint?.point?.yFormatted;
          tooltipHtml += `<div class="flex justify-between items-center">`;
          tooltipHtml += `<div><span style="color:${customPoint?.color}; font-size: 12px;">\u25CF</span>`;
          tooltipHtml += `<span class="b1 text-gray-000 ml-1">${seriesName}</span>:</div>`;
          tooltipHtml += `<span class="b1 text-gray-000">${formattedValue}</span>`;
          tooltipHtml += `</div>`;
        }
      });

      tooltipHtml += `</div>`;
      return tooltipHtml;
    };
  }
}
